import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getPackages, removePackage } from 'slice/khajurahoPackageSlice';
import AdminTable from 'common/AdminTable';
import TruncatedMessage from 'common/TruncatedMessage';
import { Link } from 'react-router-dom';
import { getUploadUrl } from 'utils/uploadUrl';

const List = () => {
    const dispatch = useDispatch();
    const packageData = useSelector((state) => state.khajurahoPackage);

    useEffect(() => {
        dispatch(getPackages());
    }, [dispatch]);

    // Backend may return multipleImages as:
    // - JSON string '["a.jpg","b.jpg"]', or
    // - comma-separated string "a.jpg,b.jpg", or
    // - already-parsed array ["a.jpg","b.jpg"], or
    // - null/undefined. Handle all cases safely.
    const parseImages = (value) => {
        if (!value) return [];
        if (Array.isArray(value)) return value.filter(Boolean);
        if (typeof value === 'string') {
            const trimmed = value.trim();
            if (!trimmed) return [];
            try {
                const parsed = JSON.parse(trimmed);
                if (Array.isArray(parsed)) return parsed.filter(Boolean);
                if (typeof parsed === 'string') return parsed ? [parsed] : [];
                return parsed ? [parsed] : [];
            } catch (e) {
                return trimmed.split(',').map((s) => s.trim()).filter(Boolean);
            }
        }
        return [];
    };

    const normalizeImg = (img) => {
        if (!img) return '';
        if (typeof img === 'string') return img;
        if (typeof img === 'object') return img.url || img.path || img.src || img.image || '';
        return String(img);
    };

    // Backend stores paths like: "khajuraho/packages/filename.jpg"
    // Backend serves them at:   /uploads/khajuraho/packages/filename.jpg
    const getImageUrl = (img) => {
        const normalized = normalizeImg(img);
        if (!normalized || typeof normalized !== 'string') return '';
        const cleanImgPath = normalized.trim().replace(/^\//, '');
        if (!cleanImgPath) return '';
        return getUploadUrl(cleanImgPath);
    };

    const tableHeaders = [
        { Header: 'ID', accessor: 'id' },
        {
            Header: 'Image',
            accessor: 'multipleImages',
            Cell: ({ row }) => {
                const images = parseImages(row.original.multipleImages).map(normalizeImg).filter(Boolean);
                return (
                    <div className="d-flex">
                        {images.slice(0, 1).map((img, index) => (
                            <img
                                key={index}
                                src={getImageUrl(img)}
                                alt={row.original.name}
                                width={70}
                                height={50}
                                style={{ objectFit: 'cover' }}
                                className="mr-1 rounded"
                            />
                        ))}
                        {images.length === 0 && <span className="text-muted small">No image</span>}
                    </div>
                );
            },
        },
        { Header: 'Name', accessor: 'name' },
        { Header: 'Tag', accessor: 'tag' },
        { Header: 'Duration', accessor: 'durationText' },
        { Header: 'Location', accessor: 'location' },
        {
            Header: 'Description',
            accessor: 'description',
            Cell: ({ row }) => <TruncatedMessage text={row.original.description} maxLength={30} />,
        },
        {
            Header: 'Actions',
            accessor: 'actions',
            Cell: ({ row }) => (
                <>
                    <button
                        className="btn removebtn mx-1 mb-2"
                        onClick={() => {
                            if (window.confirm('Are you sure you want to delete this package?')) {
                                dispatch(removePackage(row.original.id));
                            }
                        }}
                    >
                        Remove
                    </button>
                    <Link to={`/khajuraho/packages/update/${row.original.id}`} className="btn editbtn mx-1">
                        Edit
                    </Link>
                    <Link to={`/khajuraho/packages/update/${row.original.id}`} className="btn btn-sm btn-warning font-weight-bold mx-1">
                        Itinerary
                    </Link>
                </>
            ),
        },
    ];

    return (
        <div className="container-fluid mt-5">
            <div className="mainheadig mx-4 d-flex justify-content-between align-items-center">
                <h4 className="text-white font-weight-bold">Tour Packages List</h4>
                <Link to="/khajuraho/packages/add" className="btn btn-light btn-sm font-weight-bold">Add New Package</Link>
            </div>
            <div className="mt-4">
                {packageData?.loading ? (
                    <div className="text-center text-white">Loading...</div>
                ) : (
                    packageData?.data?.length > 0 ? (
                        <AdminTable tableHeaders={tableHeaders} tableData={packageData.data} />
                    ) : (
                        <div className="text-center text-white">No packages found.</div>
                    )
                )}
            </div>
        </div>
    );
};

export default List;
